// src/Pages/Home/TrendFashion/sections/NewArrivalsFashionSection.tsx
import React, { useMemo } from "react";
import { Col, Container, Image, Row } from "react-bootstrap";
import type { StoreSection } from "../../../../redux/services/store/storePagesApi";

type Props = {
  section: StoreSection;
};

const safeJson = <T,>(v: any, fallback: T): T => {
  try {
    if (!v) return fallback;
    if (typeof v === "string") return JSON.parse(v) as T;
    return (v as T) ?? fallback;
  } catch {
    return fallback;
  }
};

const NewArrivalsFashionSection: React.FC<Props> = ({ section }) => {
  const settings = useMemo(() => safeJson<any>(section.settings_json, {}), [section.settings_json]);
  const styles = (settings.styles || {}) as any;

  const items = section.items || [];
  const main = items.find((i) => i.slot === "main")?.content_json || {};

  if (!section?.is_active || !main.image_url) return null;

  return (
    <section
      className={
        styles.section_class ||
        "new-arrivals-clean-section position-relative overflow-hidden"
      }
    >
      <Container
        fluid
        className={styles.container_class || "new-arrivals-fluid-wrap container-fluid px-3 px-lg-4"}
      >
        <Row className="new-arrivals-main-row align-items-center g-4 g-lg-0">
          <Col lg={4} xl={4} className="new-arrivals-left-col">
            <div className={styles.content_class || "new-arrivals-clean-content"} />
          </Col>

          <Col lg={8} xl={8} className="new-arrivals-right-col">
            <div className={styles.image_wrap_class || "new-arrivals-banner-wrap"}>
              <Image
                src={main.image_url}
                alt={main.alt_text || "New arrivals banner"}
                className={`new-arrivals-banner-image ${styles.image_class || ""}`}
              />
            </div>
          </Col>
        </Row>
      </Container>

      <style>{`
        .new-arrivals-clean-section {
          background: linear-gradient(180deg, #fff7f8 0%, #fff1f4 100%);
        }

        .new-arrivals-clean-content {
          min-height: 1px;
          padding: 0;
        }

        .new-arrivals-banner-wrap {
          min-height: 600px;
          display: flex;
          align-items: flex-end;
          justify-content: flex-end;
          overflow: hidden;
          width: 100%;
        }

        .new-arrivals-banner-image {
          display: block;
          width: auto;
          height: 640px;
          object-fit: cover;
          object-position: center top;
          margin-right: 0 !important;
          margin-left: auto !important;
        }

        @media (min-width: 992px) {
          .new-arrivals-fluid-wrap {
            padding-right: 0 !important;
          }

          .new-arrivals-main-row {
            margin-right: 0 !important;
          }

          .new-arrivals-right-col {
            padding-right: 0 !important;
          }

          .new-arrivals-banner-wrap {
            padding-right: 0 !important;
          }
        }

        @media (max-width: 1399.98px) {
          .new-arrivals-banner-wrap {
            min-height: 560px;
          }

          .new-arrivals-banner-image {
            height: 590px;
          }
        }

        @media (max-width: 1199.98px) {
          .new-arrivals-banner-wrap {
            min-height: 510px;
          }

          .new-arrivals-banner-image {
            height: 530px;
          }
        }

        @media (max-width: 991.98px) {
          .new-arrivals-left-col {
            display: none;
          }

          .new-arrivals-right-col {
            padding-right: calc(var(--bs-gutter-x) * 0.5);
          }

          .new-arrivals-banner-wrap {
            min-height: auto;
            justify-content: center;
            align-items: center;
            margin-top: 0;
          }

          .new-arrivals-banner-image {
            width: 100%;
            height: auto;
            margin-left: 0 !important;
          }
        }

        @media (max-width: 575.98px) {
          .new-arrivals-clean-section {
            padding-bottom: 0.75rem;
          }
        }
      `}</style>
    </section>
  );
};

export default NewArrivalsFashionSection;